// src/components/IntensiveQuizPreview.js
// Quiz Preview modal for counselors to view all questions in a module
// Read-only view - counselors cannot submit answers

import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, FileQuestion, AlertTriangle } from 'lucide-react';
import { INTENSIVE_MODULE_DATA } from '../data/intensiveModules';
import { getAnswerOptions } from '../utils/answerFormatHelper';

const IntensiveQuizPreview = ({ moduleNumber, onClose }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const moduleData = INTENSIVE_MODULE_DATA[moduleNumber];

  // Get questions safely (may be undefined)
  const questions = moduleData?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  // Navigation functions wrapped in useCallback to use in useEffect
  const nextQuestion = useCallback(() => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [currentQuestionIndex, questions.length]);

  const prevQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  // Keyboard navigation - MUST be called before any conditional returns
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextQuestion();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevQuestion();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextQuestion, prevQuestion, onClose]);

  // Early return for missing data - AFTER all hooks
  if (!moduleData || !moduleData.questions) {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 text-center max-w-md">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quiz Not Available</h2>
          <p className="text-gray-600 mb-6">Quiz content for this module is not available yet.</p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gradient-to-br from-slate-800 via-slate-900 to-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 bg-black/30">
        <div className="flex items-center gap-2 sm:gap-3">
          <FileQuestion className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
          <div>
            <h3 className="text-sm sm:text-base font-semibold text-white">
              Module {moduleNumber}: {moduleData.name}
            </h3>
            <p className="text-xs text-emerald-300">
              Quiz Preview (Read-Only)
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="text-xs sm:text-sm text-gray-300">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1 bg-white/20">
        <div
          className="h-full bg-emerald-500 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Preview Notice */}
      <div className="bg-amber-900/40 border-b border-amber-600/30 px-4 py-2">
        <div className="flex items-center justify-center gap-2 text-amber-200 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span>Counselor Preview Mode - Questions are read-only</span>
        </div>
      </div>

      {/* Question Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
        <div className="max-w-3xl mx-auto">
          {/* Question Number Navigation */}
          <div className="flex justify-center gap-1.5 flex-wrap mb-6">
            {questions.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestionIndex(index)}
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm font-medium transition ${
                  index === currentQuestionIndex
                    ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {/* Question Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 sm:p-6 md:p-8 border border-white/20">
            {/* Question ID & Reverse Scoring Badge */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs font-medium text-emerald-400 bg-emerald-900/50 px-2 py-1 rounded">
                Question ID: {currentQuestion.id}
              </span>
              {currentQuestion.reverseScored && (
                <span className="text-xs font-medium text-amber-400 bg-amber-900/50 px-2 py-1 rounded">
                  ⚡ Reverse Scored
                </span>
              )}
              <span className="text-xs font-medium text-blue-400 bg-blue-900/50 px-2 py-1 rounded">
                Format: {currentQuestion.answerFormat || 'agreement'}
              </span>
            </div>

            {/* Question Text */}
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-6 leading-relaxed">
              {currentQuestion.text}
            </h2>

            {/* Insight */}
            {currentQuestion.insight && (
              <div className="mb-6 p-4 bg-amber-900/30 rounded-lg border border-amber-500/30">
                <p className="text-sm text-amber-200">
                  💡 <strong>Insight:</strong> {currentQuestion.insight}
                </p>
              </div>
            )}

            {/* Answer Options (Display Only) */}
            <div className="space-y-2 sm:space-y-3">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Answer Options:</p>
              {getAnswerOptions(currentQuestion).map((option) => (
                <div
                  key={option.value}
                  className="w-full p-3 sm:p-4 rounded-xl border border-white/20 bg-white/5 text-left"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm sm:text-base text-white">{option.text}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs sm:text-sm text-emerald-400 font-medium">
                        {option.value} pt{option.value !== 1 ? 's' : ''}
                      </span>
                      {currentQuestion.reverseScored && (
                        <span className="text-xs text-amber-400">
                          (actual: {6 - option.value})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Scoring Info */}
            <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-600/30">
              <p className="text-xs text-gray-400">
                <strong>Max Score for Module:</strong> {questions.length * 5} points ({questions.length} questions × 5 max per question)
              </p>
              {currentQuestion.reverseScored && (
                <p className="text-xs text-amber-400 mt-1">
                  <strong>Note:</strong> This question uses reverse scoring. Higher disagreement = higher score.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between p-3 sm:p-4 bg-black/30">
        <button
          onClick={prevQuestion}
          disabled={currentQuestionIndex === 0}
          className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition ${
            currentQuestionIndex === 0
              ? 'bg-white/10 text-white/50 cursor-not-allowed'
              : 'bg-white/20 text-white hover:bg-white/30'
          }`}
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Previous</span>
        </button>

        {/* Jump to question */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 hidden sm:inline">Jump to:</span>
          <select
            value={currentQuestionIndex}
            onChange={(e) => setCurrentQuestionIndex(parseInt(e.target.value))}
            className="bg-white/20 text-white border border-white/30 rounded-lg px-2 py-1 text-sm"
          >
            {questions.map((_, index) => (
              <option key={index} value={index} className="bg-gray-800">
                Q{index + 1}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={currentQuestionIndex === questions.length - 1 ? onClose : nextQuestion}
          className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition"
        >
          <span className="hidden sm:inline">
            {currentQuestionIndex === questions.length - 1 ? 'Close Preview' : 'Next'}
          </span>
          <span className="sm:hidden">
            {currentQuestionIndex === questions.length - 1 ? '✓' : '→'}
          </span>
          {currentQuestionIndex < questions.length - 1 && (
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          )}
        </button>
      </div>

      {/* Keyboard hints */}
      <div className="hidden md:flex justify-center py-2 bg-black/20 text-xs text-gray-400">
        <span>Use ← → arrow keys to navigate • Press ESC to close</span>
      </div>
    </div>
  );
};

export default IntensiveQuizPreview;